import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Input } from './input';
import { MapPin, Loader2, X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * AddressAutocomplete Component
 * 
 * Uses OpenStreetMap Nominatim via backend API for address suggestions.
 * 
 * Props:
 * - value: Current address string
 * - onChange: Callback when address is selected (receives full address object)
 * - onInputChange: Callback when input text changes (receives string)
 * - placeholder: Input placeholder text
 * - countryCodes: Comma-separated country codes (default: "au,nz,gb,us")
 * - className: Additional CSS classes
 * - disabled: Disable input
 */
export function AddressAutocomplete({
  value = '',
  onChange,
  onInputChange,
  placeholder = 'Start typing an address...',
  countryCodes = 'au,nz,gb,us',
  className,
  disabled = false,
  ...props
}) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Update input when value prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      const params = new URLSearchParams({
        q: query,
        countrycodes: countryCodes,
        limit: '6'
      });
      
      const response = await fetch(`${apiUrl}/api/geocode/autocomplete?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
        setShowDropdown(data.suggestions?.length > 0);
        setSelectedIndex(-1);
      }
    } catch (error) {
      console.error('Address autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [countryCodes]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onInputChange?.(newValue);

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  const handleSelect = (suggestion) => {
    const formattedAddress = suggestion.formatted_address || suggestion.display_name;
    setInputValue(formattedAddress);
    setSuggestions([]);
    setShowDropdown(false);
    
    onChange?.({
      formatted: formattedAddress,
      display_name: suggestion.display_name,
      lat: suggestion.lat,
      lon: suggestion.lon,
      components: suggestion.address_components
    });
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
    onInputChange?.('');
    onChange?.(null);
    inputRef.current?.focus();
  };

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setShowDropdown(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-10"
          autoComplete="off"
          data-testid="address-autocomplete-input"
          {...props}
        />
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="address-autocomplete-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {isLoading && (
          <div className="absolute right-10 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-64 overflow-auto"
          data-testid="address-autocomplete-dropdown"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id || index}
              type="button"
              className={cn(
                'w-full px-4 py-3 text-left hover:bg-accent transition-colors',
                'focus:outline-none focus:bg-accent',
                'border-b border-border last:border-b-0',
                index === selectedIndex && 'bg-accent'
              )}
              onClick={() => handleSelect(suggestion)}
              data-testid={`address-suggestion-${index}`}
            >
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {suggestion.formatted_address || suggestion.display_name}
                  </div>
                  {suggestion.address_components && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {[
                        suggestion.address_components.suburb,
                        suggestion.address_components.city,
                        suggestion.address_components.state
                      ].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;
